import { z } from 'zod';
// =========================================================
// Ensayo de esclerómetro (martillo Schmidt) — NO destructivo.
//
// Cada ensayo es N golpes sobre una zona de un elemento estructural.
// Se promedia descartando el max y el min cuando hay ≥ 3 golpes.
// La estimación de resistencia a partir de R queda fuera de esta fase.
// =========================================================
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/;
export const ANGULOS_IMPACTO = [-90, -45, 0, 45, 90];
export const AnguloImpactoSchema = z.number().int().min(-90).max(90);
// =========================================================
// Rebote individual (golpe N)
// =========================================================
export const ReboteSchema = z.object({
    id: z.number().int().positive(),
    id_ensayo: z.number().int().positive(),
    numero_golpe: z.number().int().positive(),
    valor_rebote: z.number().min(0).max(100),
    notas: z.string().nullable(),
});
export const CrearReboteRequestSchema = z.object({
    numero_golpe: z.number().int().positive().max(99),
    valor_rebote: z.number().min(0).max(100),
    notas: z.string().trim().max(300).nullable().optional(),
});
export const ActualizarReboteRequestSchema = z.object({
    valor_rebote: z.number().min(0).max(100).optional(),
    numero_golpe: z.number().int().positive().max(99).optional(),
    notas: z.string().trim().max(300).nullable().optional(),
});
// =========================================================
// Ensayo (header + rebotes en el detalle)
// =========================================================
export const EnsayoEsclerometroListadoSchema = z.object({
    id: z.number().int().positive(),
    numero: z.number().int().positive(),
    fecha: z.string(),
    obra_works_no: z.string().nullable(),
    obra_display_name: z.string().nullable(),
    id_casa: z.string().nullable(),
    elemento_estructural: z.string(),
    edad_dias: z.number().int().nullable(),
    angulo_impacto: z.number().int(),
    equipo_serial: z.string().nullable(),
    cantidad_rebotes: z.number().int(),
    /** Promedio de rebotes válido (descarta max/min cuando hay ≥3 golpes). */
    rebote_promedio: z.number().nullable(),
});
export const EnsayoEsclerometroDetalleSchema = EnsayoEsclerometroListadoSchema.extend({
    notas: z.string().nullable(),
    creado_por_email: z.string().nullable(),
    creado_en: z.string(),
    actualizado_en: z.string(),
    rebotes: z.array(ReboteSchema),
});
export const ListaEnsayosEsclerometroResponseSchema = z.object({
    ensayos: z.array(EnsayoEsclerometroListadoSchema),
    total: z.number().int(),
    pagina: z.number().int(),
    por_pagina: z.number().int(),
});
export const ListarEnsayosEsclerometroRequestSchema = z.object({
    obra_works_no: z.string().optional(),
    desde: z.string().regex(ISO_DATE).optional(),
    hasta: z.string().regex(ISO_DATE).optional(),
    q: z.string().min(1).max(100).optional(),
    pagina: z.coerce.number().int().min(1).default(1),
    por_pagina: z.coerce.number().int().min(1).max(100).default(25),
});
export const CrearEnsayoEsclerometroRequestSchema = z.object({
    fecha: z.string().regex(ISO_DATE),
    obra_works_no: z.string().trim().max(20).optional().nullable(),
    id_casa: z.string().trim().max(50).optional().nullable(),
    elemento_estructural: z.string().trim().min(1).max(100),
    edad_dias: z.number().int().positive().max(3650).optional().nullable(),
    angulo_impacto: AnguloImpactoSchema.default(0),
    equipo_serial: z.string().trim().max(50).optional().nullable(),
    notas: z.string().trim().max(2000).optional().nullable(),
});
export const ActualizarEnsayoEsclerometroRequestSchema = CrearEnsayoEsclerometroRequestSchema.partial();
/**
 * Promedio de rebotes descartando max y min cuando hay ≥3 valores. Es la
 * fórmula típica del Schmidt hammer para rechazar outliers. Compartido
 * frontend/backend para que la UI muestre el mismo valor que el listado.
 */
export function calcularReboteUtilPromedio(valores) {
    if (valores.length === 0)
        return null;
    if (valores.length === 1)
        return valores[0] ?? null;
    if (valores.length === 2)
        return (valores[0] + valores[1]) / 2;
    // 3+: descartar max y min
    const ord = [...valores].sort((a, b) => a - b);
    const conserva = ord.slice(1, -1);
    return conserva.reduce((s, v) => s + v, 0) / conserva.length;
}
//# sourceMappingURL=esclerometro.js.map